import { BoardGraph } from './graph';
import type { MapSchema } from './map-schema';
import type { BoardNode } from './types';

const DYNAMIC_SPECIAL_ELIGIBLE_TYPES = new Set([
  'gold',
  'heal',
  'hazard',
  'item',
  'event',
  'soul_anchor',
  'key_shop',
  'weapons_cache',
  'mystery_box',
]);

/**
 * Result of loading and validating a map.
 */
export interface MapLoadResult {
  /** True if the map is valid and was loaded successfully. */
  ok: boolean;
  /** The constructed BoardGraph (undefined if validation failed). */
  graph?: BoardGraph;
  /** Array of validation error strings (empty if ok). */
  errors: string[];
}

/**
 * Load a map from a MapSchema object.
 *
 * Validates:
 * 1. Schema has a non-empty name.
 * 2. Schema has version >= 1.
 * 3. Schema has at least 1 node.
 * 4. All node IDs are unique.
 * 5. All connection targetIds reference existing nodes.
 * 6. At least 3 eligible dynamic-special nodes exist.
 * 7. Legacy startingTreasureChestNodeId / startingWeaponsCacheNodeId fields are ignored.
 * 8. All spawnNodeIds reference existing nodes of type 'start'.
 * 9. At least 1 spawn node exists.
 * 10. BoardGraph.validate() returns no errors (no dead ends, etc.).
 *
 * Returns MapLoadResult with either a valid BoardGraph or an error list.
 * All validation errors are accumulated before returning (not fail-fast).
 */
export function loadMap(schema: MapSchema): MapLoadResult {
  const errors: string[] = [];

  // Check 1: non-empty name
  if (!schema.name || schema.name.trim().length === 0) {
    errors.push('Map name must be a non-empty string.');
  }

  // Check 2: version >= 1
  if (schema.version < 1) {
    errors.push(`Map version must be >= 1, got ${schema.version}.`);
  }

  // Check 3: at least 1 node
  if (!schema.nodes || schema.nodes.length === 0) {
    errors.push('Map must contain at least 1 node.');
  }

  // Build an ID lookup set for checks 4, 5, 6, 7, 8
  const nodeMap = new Map<string, (typeof schema.nodes)[number]>();
  const duplicateIds = new Set<string>();

  for (const node of schema.nodes ?? []) {
    if (nodeMap.has(node.id)) {
      duplicateIds.add(node.id);
    } else {
      nodeMap.set(node.id, node);
    }
  }

  // Check 4: unique node IDs
  for (const id of duplicateIds) {
    errors.push(`Duplicate node ID: ${id}.`);
  }

  // Check 5: all connection targets exist (only check nodes we have uniquely — duplicates handled above)
  for (const node of schema.nodes ?? []) {
    for (const conn of node.connections) {
      if (!nodeMap.has(conn.targetId)) {
        errors.push(
          `Dangling connection: node "${node.id}" references non-existent target "${conn.targetId}".`,
        );
      }
    }
  }

  // Check 6: dynamic chest/cache spawn needs enough eligible goal nodes.
  const eligibleDynamicSpecialNodes = Array.from(nodeMap.values()).filter(
    (node) => node.supportsGoal && DYNAMIC_SPECIAL_ELIGIBLE_TYPES.has(node.type),
  );
  if (eligibleDynamicSpecialNodes.length < 3) {
    errors.push(
      `Map must contain at least 3 eligible dynamic chest/cache spawn nodes, got ${eligibleDynamicSpecialNodes.length}. Eligible nodes must have supportsGoal: true and type gold, heal, hazard, item, event, soul_anchor, key_shop, weapons_cache, or mystery_box.`,
    );
  }

  // Check 9: at least 1 spawn node
  if (!schema.spawnNodeIds || schema.spawnNodeIds.length === 0) {
    errors.push('Map must have at least 1 spawn node in spawnNodeIds.');
  }

  // Check 8: all spawn nodes exist + are type 'start'
  for (const spawnId of schema.spawnNodeIds ?? []) {
    const spawnNode = nodeMap.get(spawnId);
    if (!spawnNode) {
      errors.push(`spawnNodeIds references non-existent node "${spawnId}".`);
    } else if (spawnNode.type !== 'start') {
      errors.push(
        `spawnNodeIds "${spawnId}" has type "${spawnNode.type}" but must be type "start".`,
      );
    }
  }

  // If we have structural errors before this point, graph construction would fail
  // or give misleading check-10 errors — return early.
  if (errors.length > 0) {
    return { ok: false, errors };
  }

  // Check 10: BoardGraph.validate() — catches dead ends and any remaining invariants
  const boardNodes: BoardNode[] = schema.nodes.map((n) => ({ ...n }));
  let graph: BoardGraph;
  try {
    graph = new BoardGraph(boardNodes);
  } catch (err) {
    // Constructor throws on duplicate IDs or dangling connections —
    // these should have been caught above, but guard defensively.
    errors.push(
      `BoardGraph construction failed: ${err instanceof Error ? err.message : String(err)}`,
    );
    return { ok: false, errors };
  }

  const graphErrors = graph.validate();
  if (graphErrors.length > 0) {
    return { ok: false, errors: graphErrors };
  }

  return { ok: true, graph, errors: [] };
}
